/**
 * Utilitaires de formatage centralisés
 * Fonctions pures qui peuvent être utilisées dans les hooks React et les fonctions utilitaires
 */

export type Language = 'fr' | 'en';

/**
 * Retourne la locale string selon la langue
 */
export const getLocaleString = (language: Language): string => {
  return language === 'fr' ? 'fr-FR' : 'en-US';
};

/**
 * Retourne le format de date court selon la langue
 */
export const getDateFormatString = (language: Language): string => {
  return language === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
};

/** Compact card dates: 2-digit year (dd/MM/yy) — enough for rights/payment horizons */
export const getDateFormatStringCompact = (language: Language): string => {
  return language === 'fr' ? 'dd/MM/yy' : 'MM/dd/yy';
};

/**
 * Formate une date en format court selon la langue
 */
export const formatDateShort = (date: Date | string, language: Language): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const locale = getLocaleString(language);
  return dateObj.toLocaleDateString(locale);
};

/**
 * Formate une date en format long selon la langue
 */
export const formatDateLong = (date: Date | string, language: Language): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const locale = getLocaleString(language);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dateObj);
};

/**
 * Formate une monnaie selon la langue
 */
export const formatCurrencyValue = (
  value: number,
  language: Language,
  currency: string = 'EUR',
  options?: Intl.NumberFormatOptions
): string => {
  const locale = getLocaleString(language);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    ...options,
  }).format(value);
};

/**
 * Formate un nombre selon la langue
 */
export const formatNumberValue = (
  value: number,
  language: Language,
  options?: Intl.NumberFormatOptions
): string => {
  const locale = getLocaleString(language);
  return new Intl.NumberFormat(locale, options).format(value);
};

